import { ref, push, set, serverTimestamp, get, query, orderByChild, equalTo, limitToLast, update, onValue, remove, runTransaction, increment } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { NotificationService } from '../../notifications/services/NotificationService';
import { OfflineQueueService } from './OfflineQueueService';
import { ChatAuditService } from './ChatAuditService';

export const MessageService = {
    /**
     * Generate a unique client message ID for dedup.
     */
    _generateClientMessageId: () => {
        return `cmid_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
    },

    // Send Message with clientMessageId dedup and full integrity metadata
    sendMessage: async (chatId, messageData) => {
        try {
            // STRICT GUARD: Reject empty/malformed IDs — allow both 1:1 (uid_uid) and group (push keys starting with -)
            if (!chatId || chatId.length < 5) {
                console.error("MessageService: Refusing to send to invalid Chat ID:", chatId);
                return { success: false, error: "Invalid Chat ID" };
            }

            // Generate or reuse clientMessageId (retry must reuse)
            const clientMessageId = messageData.clientMessageId || MessageService._generateClientMessageId();

            // OFFLINE HANDLING
            if (!navigator.onLine) {
                console.log("Network offline. Queuing message.");
                OfflineQueueService.addToQueue({
                    chatId,
                    messageData: { ...messageData, clientMessageId },
                    tempId: clientMessageId
                });
                return { success: true, messageId: clientMessageId, status: 'pending_offline', clientMessageId };
            }

            // DUPLICATE CHECK: If retrying, check if message already exists
            if (messageData.clientMessageId) {
                try {
                    const dupQuery = query(
                        ref(db, `conversations/${chatId}/messages`),
                        orderByChild('clientMessageId'),
                        equalTo(clientMessageId)
                    );
                    const dupSnap = await get(dupQuery);
                    if (dupSnap.exists()) {
                        console.log('[MessageService] Duplicate clientMessageId detected, skipping:', clientMessageId);
                        const existingKey = Object.keys(dupSnap.val())[0];
                        return { success: true, messageId: existingKey, clientMessageId, deduplicated: true };
                    }
                } catch (dupErr) {
                    // If dedup check fails, proceed anyway (better to send than lose)
                    console.warn('[MessageService] Dedup check failed, proceeding:', dupErr.message);
                }
            }

            const messagesRef = ref(db, `conversations/${chatId}/messages`);
            const newMessageRef = push(messagesRef);

            const payload = {
                ...messageData,
                clientMessageId,
                chatId,
                createdAt: Date.now(),
                timestamp: serverTimestamp(),
                read: false,
                status: 'sent',
                sentAt: serverTimestamp(),
                retryCount: messageData.retryCount || 0
            };

            await set(newMessageRef, payload);

            // Update Chat Thread Metadata
            await set(ref(db, `conversations/${chatId}/lastMessage`), {
                text: messageData.type === 'text' ? messageData.text : `[${messageData.type}]`,
                timestamp: serverTimestamp(),
                senderId: messageData.senderId,
                status: 'sent'
            });

            // Check for Mentions
            if (messageData.text && messageData.text.includes('@')) {
                if (messageData.mentions && Array.isArray(messageData.mentions)) {
                    messageData.mentions.forEach(mentionedUserId => {
                        NotificationService.send(mentionedUserId, {
                            type: 'chat_mention',
                            title: `${messageData.senderName || 'Someone'} mentioned you`,
                            message: messageData.text,
                            link: `/dashboard/chat/${chatId}`,
                            meta: { chatId, senderId: messageData.senderId }
                        });
                    });
                }
            }

            // UNREAD COUNTERS. Nothing used to write these: ChatList read
            // conversations/{id}/unread/{uid} but no code ever incremented or cleared it,
            // so every unread badge in the app was permanently empty. Incremented for
            // everyone EXCEPT the sender, atomically, so two simultaneous messages cannot
            // lose a count.
            try {
                const recipients = await MessageService._recipientsOf(chatId, messageData.senderId);
                if (recipients.length) {
                    const bump = {};
                    recipients.forEach(uid => { bump[`${uid}`] = increment(1); });
                    await update(ref(db, `conversations/${chatId}/unread`), bump);
                }
            } catch (unreadErr) {
                // A badge is not worth failing a delivered message over.
                console.warn('[MessageService] unread bump failed:', unreadErr.message);
            }

            // Remove from failed queue if this was a retry
            if (messageData.clientMessageId) {
                OfflineQueueService.removeFailedMessage(clientMessageId);
            }

            return { success: true, messageId: newMessageRef.key, clientMessageId };
        } catch (error) {
            console.error("Error sending message:", error);
            // Store in failed queue for retry
            const cid = messageData.clientMessageId || MessageService._generateClientMessageId();
            OfflineQueueService.addFailedMessage(chatId, { ...messageData, clientMessageId: cid }, error.message);
            return { success: false, error: error.message, clientMessageId: cid, status: 'failed' };
        }
    },

    // Sync Offline Messages
    syncOfflineMessages: async () => {
        if (!navigator.onLine) return;

        const queue = OfflineQueueService.getQueue();
        if (queue.length === 0) return;

        console.log(`Syncing ${queue.length} offline messages...`);

        for (const item of queue) {
            try {
                const { chatId, messageData, tempId } = item;

                const messagesRef = ref(db, `conversations/${chatId}/messages`);
                const newMessageRef = push(messagesRef);

                const payload = {
                    ...messageData,
                    timestamp: serverTimestamp(),
                    read: false,
                    status: 'sent',
                    sentAt: serverTimestamp(),
                    syncedFromOffline: true
                };

                await set(newMessageRef, payload);

                // Update Metadata
                await set(ref(db, `conversations/${chatId}/lastMessage`), {
                    text: messageData.type === 'text' ? messageData.text : `[${messageData.type}]`,
                    timestamp: serverTimestamp(),
                    senderId: messageData.senderId,
                    status: 'sent'
                });

                // Remove from queue on success
                OfflineQueueService.removeFromQueue(tempId);

            } catch (error) {
                console.error("Error syncing message:", error);
            }
        }
    },

    /**
     * Everyone in the conversation except the sender.
     *
     * A 1:1 chat id is `uidA_uidB`, so the other party is derivable without a read. A
     * group has a members node. Falls back to an empty list rather than guessing — a
     * wrong recipient would show a stranger an unread badge for a chat they are not in.
     */
    _recipientsOf: async (chatId, senderId) => {
        const id = String(chatId || '');
        const others = (map) => Object.keys(map || {}).filter(uid => uid && map[uid] === true && uid !== senderId);
        // ⚠️ Group and channel ids are push keys, which always start with '-' and CAN
        // contain '_' (it is in the push-key alphabet — roughly one group id in six has
        // one). This used to test `includes('_')`, so such a group was split like a DM
        // and its real members never got an unread badge. User ids never start with '-'.
        if (!id.startsWith('-')) {
            return id.split('_').filter(uid => uid && uid !== senderId);
        }
        // A channel's members live in channels/{id} (written only by the server);
        // a group's in the conversation itself.
        const chSnap = await get(ref(db, `channels/${id}/members`));
        if (chSnap.exists()) return others(chSnap.val());
        const snap = await get(ref(db, `conversations/${id}/members`));
        if (!snap.exists()) return [];
        return others(snap.val());
    },

    /**
     * The reader has seen this conversation: advance the receipts and clear their badge.
     *
     * `markAsSeen` existed but NOTHING EVER CALLED IT, so every message sat at
     * status 'sent' for ever and the tick in the bubble could never advance past a single
     * grey check. This is the call that makes receipts mean something.
     *
     * One multi-path update, not one write per message: a conversation opened after fifty
     * incoming messages would otherwise fire fifty round trips.
     *
     * @param {string} chatId
     * @param {string} readerUid
     * @param {object[]} messages  the messages already on screen — no extra read
     * @returns {Promise<number>} how many were newly marked seen
     */
    markIncomingSeen: async (chatId, readerUid, messages = []) => {
        if (!chatId || !readerUid) return 0;
        const unseen = (messages || []).filter(m =>
            m && m.id
            && m.senderId && m.senderId !== readerUid       // only what others sent
            && m.status !== 'seen' && !m.seen);             // and only what is not already seen
        const patch = {};
        unseen.forEach(m => {
            patch[`messages/${m.id}/status`] = 'seen';
            patch[`messages/${m.id}/seenAt`] = serverTimestamp();
            patch[`messages/${m.id}/read`] = true;
            // Per-member, so a GROUP can show who has read it — a single status field
            // cannot answer "read by whom" once there is more than one recipient.
            patch[`messages/${m.id}/readBy/${readerUid}`] = Date.now();
        });
        // Clearing the badge happens even when there is nothing new to mark — the counter
        // and the messages can drift, and an open conversation must not keep a badge.
        patch[`unread/${readerUid}`] = 0;
        try {
            await update(ref(db, `conversations/${chatId}`), patch);
            return unseen.length;
        } catch (error) {
            console.error('Error marking conversation seen:', error);
            return 0;
        }
    },

    // Mark as Delivered
    markAsDelivered: async (chatId, messageId) => {
        try {
            await update(ref(db, `conversations/${chatId}/messages/${messageId}`), {
                status: 'delivered',
                deliveredAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error marking delivered:", error);
        }
    },

    // Mark as Seen
    markAsSeen: async (chatId, messageId) => {
        try {
            await update(ref(db, `conversations/${chatId}/messages/${messageId}`), {
                status: 'seen',
                seenAt: serverTimestamp(),
                read: true
            });
        } catch (error) {
            console.error("Error marking seen:", error);
        }
    },

    // Get Messages (Initial Load)
    getMessages: async (chatId, limit = 50) => {
        try {
            const messagesRef = ref(db, `conversations/${chatId}/messages`);
            const q = query(messagesRef, orderByChild('timestamp'), limitToLast(limit));
            const snapshot = await get(q);

            if (snapshot.exists()) {
                const data = snapshot.val();
                return Object.keys(data).map(key => ({ ...data[key], id: key }));
            }
            return [];
        } catch (error) {
            console.error("Error fetching messages:", error);
            return [];
        }
    },

    /**
     * Soft Delete — NEVER removes data from Firebase.
     * @param {'for_me' | 'for_everyone'} deleteType
     */
    deleteMessage: async (chatId, messageId, userId, deleteType = 'for_everyone') => {
        try {
            // Fetch message preview for audit
            let messagePreview = '';
            try {
                const msgSnap = await get(ref(db, `conversations/${chatId}/messages/${messageId}/text`));
                messagePreview = msgSnap.val() || '';
            } catch (_) { /* non-critical */ }

            const updates = {};

            if (deleteType === 'for_everyone') {
                // Global soft delete — visible to all as "deleted"
                updates[`conversations/${chatId}/messages/${messageId}/deleted`] = true;
                updates[`conversations/${chatId}/messages/${messageId}/deletedAt`] = Date.now();
                updates[`conversations/${chatId}/messages/${messageId}/deletedBy`] = userId;
                updates[`conversations/${chatId}/messages/${messageId}/deleteType`] = 'for_everyone';
            } else {
                // Per-user delete — only hide for this user
                updates[`conversations/${chatId}/messages/${messageId}/deletedFor/${userId}`] = true;
            }

            await update(ref(db), updates);

            // Audit log (non-blocking)
            ChatAuditService.logMessageDeleted(chatId, {
                actorId: userId,
                actorName: '',
                messageId,
                deleteType,
                messagePreview: messagePreview.slice(0, 100)
            });

            return { success: true };
        } catch (error) {
            console.error("Error soft-deleting message:", error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Edit Message — preserves edit history.
     */
    editMessage: async (chatId, messageId, newText, editedByUserId) => {
        try {
            // Fetch old text for history
            const msgSnap = await get(ref(db, `conversations/${chatId}/messages/${messageId}/text`));
            const oldText = msgSnap.val() || '';

            // Push edit history record
            const editHistoryRef = push(ref(db, `conversations/${chatId}/messages/${messageId}/editHistory`));

            const updates = {};
            updates[`conversations/${chatId}/messages/${messageId}/text`] = newText;
            updates[`conversations/${chatId}/messages/${messageId}/edited`] = true;
            updates[`conversations/${chatId}/messages/${messageId}/editedAt`] = Date.now();
            updates[`conversations/${chatId}/messages/${messageId}/editHistory/${editHistoryRef.key}`] = {
                oldText,
                newText,
                editedBy: editedByUserId,
                editedAt: Date.now()
            };

            await update(ref(db), updates);

            // Audit log (non-blocking)
            ChatAuditService.logMessageEdited(chatId, {
                actorId: editedByUserId,
                actorName: '',
                messageId,
                oldText: oldText.slice(0, 100),
                newText: newText.slice(0, 100)
            });

            return { success: true };
        } catch (error) {
            console.error("Error editing message:", error);
            return { success: false, error: error.message };
        }
    },

    // Set Typing Status
    setTypingStatus: async (chatId, userId, isTyping) => {
        try {
            await set(ref(db, `conversations/${chatId}/typing/${userId}`), isTyping ? true : null);
        } catch (error) {
            console.error("Error setting typing status:", error);
        }
    },

    // Mark Message as Read
    markAsRead: async (chatId, messageId, userId) => {
        try {
            await update(ref(db, `conversations/${chatId}/messages/${messageId}`), {
                read: true,
                readBy: { [userId]: true }
            });
        } catch (error) {
            console.error("Error marking message as read:", error);
        }
    },

    /**
     * Pin a message — Telegram-style with full metadata.
     * Permission enforced at service layer via GroupService.canPin().
     * @param {string} chatId
     * @param {Object} message - Full message object { id, text, senderId, senderName, type, timestamp }
     * @param {Object} pinnedByUser - { uid, name }
     */
    pinMessage: async (chatId, message, pinnedByUser) => {
        try {
            // Service-layer permission enforcement
            const { GroupService } = await import('./GroupService');
            const allowed = await GroupService.canPin(chatId, pinnedByUser.uid);
            if (!allowed) {
                return { success: false, error: 'You do not have permission to pin messages in this chat.' };
            }

            const pinRecord = {
                messageId: message.id,
                text: (message.text || '').slice(0, 100),
                senderId: message.senderId,
                senderName: message.senderName || 'Unknown',
                pinnedBy: pinnedByUser.uid,
                pinnedByName: pinnedByUser.name || '',
                pinnedAt: Date.now(),
                messageType: message.type || 'text',
                originalTimestamp: message.timestamp || Date.now()
            };

            const updates = {};
            updates[`conversations/${chatId}/pinnedMessages/${message.id}`] = pinRecord;
            updates[`conversations/${chatId}/messages/${message.id}/pinned`] = true;

            await update(ref(db), updates);

            // Transaction-safe latestPinnedMessage update
            const latestRef = ref(db, `conversations/${chatId}/latestPinnedMessage`);
            await runTransaction(latestRef, (current) => {
                // Always set to newest pin
                if (!current || (pinRecord.pinnedAt >= (current.pinnedAt || 0))) {
                    return pinRecord;
                }
                return current;
            });

            // Audit (non-blocking)
            ChatAuditService.logMessagePinned(chatId, {
                actorId: pinnedByUser.uid,
                actorName: pinnedByUser.name || '',
                messageId: message.id,
                messagePreview: (message.text || '').slice(0, 60)
            });

            return { success: true };
        } catch (error) {
            console.error("Error pinning message:", error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Unpin a message. Recalculates latestPinnedMessage from remaining pins.
     * Permission enforced at service layer.
     */
    unpinMessage: async (chatId, messageId, unpinnedByUserId) => {
        try {
            const { GroupService } = await import('./GroupService');
            const allowed = await GroupService.canPin(chatId, unpinnedByUserId);
            if (!allowed) {
                return { success: false, error: 'You do not have permission to unpin messages in this chat.' };
            }

            const updates = {};
            updates[`conversations/${chatId}/pinnedMessages/${messageId}`] = null;
            updates[`conversations/${chatId}/messages/${messageId}/pinned`] = null;

            // Recalculate latestPinnedMessage
            const allPinsSnap = await get(ref(db, `conversations/${chatId}/pinnedMessages`));
            const allPins = allPinsSnap.val() || {};
            delete allPins[messageId];

            await update(ref(db), updates);

            // Transaction-safe latestPinnedMessage recalculation
            const latestRef = ref(db, `conversations/${chatId}/latestPinnedMessage`);
            const remaining = Object.values(allPins);
            await runTransaction(latestRef, () => {
                if (remaining.length > 0) {
                    remaining.sort((a, b) => (b.pinnedAt || 0) - (a.pinnedAt || 0));
                    return remaining[0];
                }
                return null;
            });

            // Audit (non-blocking)
            ChatAuditService.logMessageUnpinned(chatId, {
                actorId: unpinnedByUserId,
                actorName: '',
                messageId
            });

            return { success: true };
        } catch (error) {
            console.error("Error unpinning message:", error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Unpin all messages in a chat. Owner/admin only in groups.
     */
    unpinAllMessages: async (chatId, userId) => {
        try {
            const { GroupService } = await import('./GroupService');
            const allowed = await GroupService.canPin(chatId, userId);
            if (!allowed) {
                return { success: false, error: 'Permission denied.' };
            }

            // Get all pinned message IDs to clear their pinned flag
            const pinsSnap = await get(ref(db, `conversations/${chatId}/pinnedMessages`));
            const pins = pinsSnap.val() || {};

            const updates = {};
            updates[`conversations/${chatId}/pinnedMessages`] = null;
            updates[`conversations/${chatId}/latestPinnedMessage`] = null;

            Object.keys(pins).forEach(msgId => {
                updates[`conversations/${chatId}/messages/${msgId}/pinned`] = null;
            });

            await update(ref(db), updates);
            return { success: true };
        } catch (error) {
            console.error("Error unpinning all messages:", error);
            return { success: false, error: error.message };
        }
    },

    // Toggle Reaction
    toggleReaction: async (chatId, messageId, userId, emoji) => {
        try {
            const reactionRef = ref(db, `conversations/${chatId}/messages/${messageId}/reactions/${userId}`);
            const snapshot = await get(reactionRef);
            
            // Clean up any legacy reactions for this user to prevent "ghost" reactions
            // The old format was: reactions/{emoji}/{userId} = true
            const allReactionsRef = ref(db, `conversations/${chatId}/messages/${messageId}/reactions`);
            const allSnap = await get(allReactionsRef);
            if (allSnap.exists()) {
                const allData = allSnap.val();
                const updates = {};
                Object.keys(allData).forEach(key => {
                    if (typeof allData[key] === 'object' && allData[key] !== null) {
                        if (allData[key][userId]) {
                            updates[`conversations/${chatId}/messages/${messageId}/reactions/${key}/${userId}`] = null;
                        }
                    }
                });
                if (Object.keys(updates).length > 0) {
                    await update(ref(db), updates);
                }
            }

            if (snapshot.exists() && snapshot.val() === emoji) {
                // Remove if same
                await set(reactionRef, null);
            } else {
                // Set new
                await set(reactionRef, emoji);
            }
            return { success: true };
        } catch (error) {
            console.error("Error toggling reaction:", error);
            return { success: false, error: error.message };
        }
    },

    // Schedule Reminder
    scheduleReminder: async (userId, messageId, note, timestamp) => {
        try {
            const reminderRef = push(ref(db, `reminders/${userId}`));
            await set(reminderRef, {
                messageId,
                note,
                timestamp,
                createdAt: serverTimestamp(),
                status: 'pending'
            });
            return { success: true };
        } catch (error) {
            console.error("Error scheduling reminder:", error);
            return { success: false, error: error.message };
        }
    },

    // Create Task from Message
    createTaskFromMessage: async ({ messageId, messageText, chatId, createdBy, assignedTo, dueDate }) => {
        try {
            const tasksRef = ref(db, 'tasks');
            const newTaskRef = push(tasksRef);
            const taskId = newTaskRef.key;

            const assignedUser = assignedTo || createdBy;

            const task = {
                taskId,
                title: messageText.slice(0, 40),
                description: messageText,
                chatReference: { chatId, messageId: messageId || null },
                createdBy,
                assignedTo: assignedUser,
                participants: {
                    [createdBy]: true,
                    [assignedUser]: true
                },
                status: 'pending',
                dueDate: dueDate || null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            await set(newTaskRef, task);

            // Log initial activity
            await MessageService.logTaskActivity(taskId, 'created', createdBy);

            // Send Auto-Message in Chat
            await MessageService.sendMessage(chatId, {
                text: `Task created: ${task.title}`,
                senderId: createdBy,
                type: 'task_link',
                taskId: taskId
            });

            // Notify Assignee if different from Creator
            if (assignedUser && assignedUser !== createdBy) {
                NotificationService.send(assignedUser, {
                    type: 'task_assigned',
                    title: 'New Task Assigned',
                    message: `${createdBy} assigned '${task.title}' to you`,
                    link: `/dashboard/todo`,
                    meta: { taskId, assignedBy: createdBy }
                });
            }

            return { success: true, taskId };
        } catch (error) {
            console.error("Error creating task:", error);
            return { success: false, error: error.message };
        }
    },

    // Log Task Activity
    logTaskActivity: async (taskId, action, userId, details = null) => {
        try {
            const historyRef = push(ref(db, `tasks/${taskId}/history`));
            await set(historyRef, {
                type: action,
                by: userId,
                timestamp: serverTimestamp(),
                ...details
            });
        } catch (error) {
            console.error("Error logging task activity:", error);
        }
    },

    // Complete Task Helper
    completeTask: async (taskId, userId) => {
        try {
            await update(ref(db, `tasks/${taskId}`), {
                status: 'completed',
                completedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                reminderTriggered: true
            });
            await MessageService.logTaskActivity(taskId, 'completed', userId);

            // Notify Creator (if not self)
            const taskSnapshot = await get(ref(db, `tasks/${taskId}`));
            if (taskSnapshot.exists()) {
                const task = taskSnapshot.val();
                if (task.createdBy && task.createdBy !== userId) {
                    NotificationService.send(task.createdBy, {
                        type: 'task_completed',
                        title: 'Task Completed',
                        message: `Task '${task.title}' was completed`,
                        link: `/dashboard/todo`,
                        meta: { taskId, completedBy: userId }
                    });
                }
            }

            return { success: true };
        } catch (error) {
            console.error("Error completing task:", error);
            return { success: false, error: error.message };
        }
    },

    // Assign Task Helper
    assignTask: async (taskId, assignedToId, assignedById) => {
        try {
            // Updated to handle participants
            const taskStartSnapshot = await get(ref(db, `tasks/${taskId}`));
            if (!taskStartSnapshot.exists()) throw new Error('Task not found');

            const taskData = taskStartSnapshot.val();
            const participants = taskData.participants || {};
            participants[assignedToId] = true; // Add new assignee

            await update(ref(db, `tasks/${taskId}`), {
                assignedTo: assignedToId,
                participants: participants,
                updatedAt: serverTimestamp()
            });

            await MessageService.logTaskActivity(taskId, 'assigned', assignedById, { to: assignedToId });

            // Notify
            if (assignedToId !== assignedById) {
                NotificationService.send(assignedToId, {
                    type: 'task_assigned',
                    title: 'Task Assigned',
                    message: `You were assigned '${taskData.title}'`,
                    link: `/dashboard/todo`,
                    meta: { taskId, assignedBy: assignedById }
                });
            }

            return { success: true };
        } catch (error) {
            console.error("Error assigning task:", error);
            return { success: false, error: error.message };
        }
    },

    // Update Task Helper
    updateTask: async (taskId, updates, userId) => {
        try {
            await update(ref(db, `tasks/${taskId}`), {
                ...updates,
                updatedAt: serverTimestamp()
            });
            await MessageService.logTaskActivity(taskId, 'updated', userId);
            return { success: true };
        } catch (error) {
            console.error("Error updating task:", error);
            return { success: false, error: error.message };
        }
    },

    // Schedule Message
    scheduleMessage: async (chatId, messageObj, scheduledAt) => {
        try {
            const scheduledRef = push(ref(db, `scheduled_messages/${chatId}`));
            await set(scheduledRef, {
                ...messageObj,
                scheduledAt,
                status: 'pending',
                createdAt: serverTimestamp()
            });
            return { success: true };
        } catch (error) {
            console.error("Error scheduling message:", error);
            return { success: false, error: error.message };
        }
    },

    // Poll Scheduled Messages (Realtime listener only — no interval polling)
    pollScheduledMessages: (chatId, onDueMessage) => {
        const scheduledRef = ref(db, `scheduled_messages/${chatId}`);

        // Single realtime listener checks for due messages
        const unsubscribe = onValue(scheduledRef, (snapshot) => {
            const data = snapshot.val();
            if (!data) return;
            const now = Date.now();
            Object.entries(data).forEach(([key, msg]) => {
                if (msg.status === 'pending' && msg.scheduledAt <= now) {
                    if (onDueMessage) onDueMessage(msg, key);
                }
            });
        });

        return () => {
            unsubscribe();
        };
    },

    // Process (Send) Scheduled Message — with atomic transaction lock
    processScheduledMessage: async (chatId, scheduledMsgId, messageData) => {
        const scheduledMsgRef = ref(db, `scheduled_messages/${chatId}/${scheduledMsgId}`);

        try {
            // Atomic lock: only claim if status is still 'pending'
            const result = await runTransaction(scheduledMsgRef, (currentData) => {
                if (!currentData || currentData.status !== 'pending') {
                    // Already claimed by another client or doesn't exist — abort
                    return undefined;
                }
                // Claim it
                return { ...currentData, status: 'sending' };
            });

            // If transaction was aborted (another client claimed it), skip
            if (!result.committed) {
                return;
            }

            // Send as real message
            await MessageService.sendMessage(chatId, {
                text: messageData.text,
                senderId: messageData.senderId,
                senderName: messageData.senderName,
                type: messageData.type,
                fileUrl: messageData.fileUrl || null
            });

            // Mark as sent
            await update(scheduledMsgRef, {
                status: 'sent',
                sentAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error processing scheduled message:", error);
            // Mark as failed so it doesn't retry infinitely
            try {
                await update(scheduledMsgRef, {
                    status: 'failed',
                    error: error.message,
                    failedAt: serverTimestamp()
                });
            } catch (updateErr) {
                console.error("Failed to mark scheduled message as failed:", updateErr);
            }
        }
    },

    // Cancel Scheduled Message
    cancelScheduledMessage: async (chatId, messageId) => {
        try {
            await remove(ref(db, `scheduled_messages/${chatId}/${messageId}`));
            return { success: true };
        } catch (error) {
            console.error("Error cancelling scheduled message:", error);
            return { success: false, error: error.message };
        }
    }
};
